import axios from 'axios';

// In development, use '/api' which is proxied by Vite dev server
// In production, use the full API URL from environment variable
const rawApiUrl = import.meta.env.VITE_API_URL || '';
const API_BASE_URL = rawApiUrl
  ? (rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`)
  : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true, // Send HttpOnly cookies with every request
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Don't redirect for auth endpoints - they handle their own errors:
      //   /auth/login, /auth/setup: show form-level errors
      //   /auth/me: handled by AuthContext.checkAuth() (initial session check)
      const isAuthEndpoint = requestUrl.includes('/auth/login')
        || requestUrl.includes('/auth/setup')
        || requestUrl.includes('/auth/me');
      if (!isAuthEndpoint) {
        localStorage.removeItem('adminToken');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login?expired=true';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Admin API services
export const adminApi = {
  // Dashboard & Analytics
  getDashboard: () => api.get('/admin/analytics/dashboard'),
  getAnalytics: (params) => api.get('/admin/analytics/dashboard', { params }),
  exportAnalytics: (params) => api.get('/admin/analytics/export', { params, responseType: 'blob' }),
  getAiOverview: () => api.get('/admin/ai/overview'),

  // Tenants
  getTenants: (params) => api.get('/admin/tenants', { params }),
  getTenant: (id) => api.get(`/admin/tenants/${id}`),
  createTenant: (data) => api.post('/admin/tenants', data),
  updateTenant: (id, data) => api.put(`/admin/tenants/${id}`, data),
  suspendTenant: (id, reason) => api.post(`/admin/tenants/${id}/suspend`, { reason }),
  activateTenant: (id) => api.post(`/admin/tenants/${id}/activate`),
  changePlan: (id, data) => api.post(`/admin/tenants/${id}/change-plan`, data),
  extendTrial: (id, days) => api.post(`/admin/tenants/${id}/extend-trial`, { days }),
  getTenantLoginLink: (id) => api.get(`/admin/tenants/${id}/login-link`),
  deleteTenant: (id) => api.delete(`/admin/tenants/${id}`),
  syncTenantFromNeon: (data) => api.post('/admin/tenants/sync-from-neon', data),

  // Subscriptions
  getPlans: () => api.get('/admin/subscriptions/plans'),
  createPlan: (data) => api.post('/admin/subscriptions/plans', data),
  updatePlan: (id, data) => api.put(`/admin/subscriptions/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/admin/subscriptions/plans/${id}`),
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  getSubscriptionStats: () => api.get('/admin/subscriptions/stats'),
  getInvoices: (params) => api.get('/admin/subscriptions/invoices', { params }),
  getInvoiceStats: () => api.get('/admin/subscriptions/invoice-stats'),
  downloadInvoice: (id) => api.get(`/admin/subscriptions/invoices/${id}/pdf`, { responseType: 'blob' }),
  exportInvoices: (params) => api.get('/admin/subscriptions/invoices/export', { params, responseType: 'blob' }),
  getPayments: (params) => api.get('/admin/subscriptions/payments', { params }),
  seedDefaults: () => api.post('/admin/subscriptions/seed'),
  processRefund: (data) => api.post('/admin/subscriptions/refund', data),

  // Add-ons
  getAddOns: () => api.get('/admin/addons'),
  getAddons: () => api.get('/admin/addons'),
  createAddOn: (data) => api.post('/admin/addons', data),
  updateAddOn: (id, data) => api.put(`/admin/addons/${id}`, data),
  deleteAddOn: (id) => api.delete(`/admin/addons/${id}`),
  getTenantAddOns: (tenantId) => api.get(`/admin/tenants/${tenantId}/addons`),
  getTenantAddons: (tenantId) => api.get(`/admin/tenants/${tenantId}/addons`),
  toggleTenantAddon: (tenantId, addonId, enabled) => api.post(`/admin/tenants/${tenantId}/addons`, { addonId, enabled }),
  enableTenantAddOn: (tenantId, addOnId) => api.post(`/admin/addons/tenant/${tenantId}/enable`, { addOnId }),
  disableTenantAddOn: (tenantId, addOnId) => api.post(`/admin/addons/tenant/${tenantId}/disable`, { addOnId }),
  updateTenantFeatures: (tenantId, features) => api.put(`/admin/tenants/${tenantId}/features`, { features }),
  // Multi-firm staff: addresses from other practices allowed to sign in here (super-admin only)
  getTenantMemberships: (tenantId) => api.get(`/admin/tenants/${tenantId}/memberships`),
  grantTenantMembership: (tenantId, data) => api.post(`/admin/tenants/${tenantId}/memberships`, data),
  revokeTenantMembership: (tenantId, membershipId) => api.delete(`/admin/tenants/${tenantId}/memberships/${membershipId}`),
  getPlanAddOns: (planId) => api.get(`/admin/addons/plan/${planId}`),
  updatePlanAddOns: (planId, addOnIds) => api.put(`/admin/addons/plan/${planId}`, { addOnIds }),

  // Sales
  getLeads: (params) => api.get('/admin/sales/leads', { params }),
  getLead: (id) => api.get(`/admin/sales/leads/${id}`),
  createLead: (data) => api.post('/admin/sales/leads', data),
  updateLead: (id, data) => api.put(`/admin/sales/leads/${id}`, data),
  convertLead: (id, data) => api.post(`/admin/sales/leads/${id}/convert`, data),
  convertLeadToOpportunity: (id) => api.post(`/admin/sales/leads/${id}/convert`),
  deleteLead: (id) => api.delete(`/admin/sales/leads/${id}`),

  getOpportunities: (params) => api.get('/admin/sales/opportunities', { params }),
  getOpportunityStats: () => api.get('/admin/sales/opportunities/stats'),
  getPipeline: (params) => api.get('/admin/sales/opportunities/pipeline', { params }),
  createOpportunity: (data) => api.post('/admin/sales/opportunities', data),
  updateOpportunity: (id, data) => api.put(`/admin/sales/opportunities/${id}`, data),
  deleteOpportunity: (id) => api.delete(`/admin/sales/opportunities/${id}`),
  moveStage: (id, data) => api.put(`/admin/sales/opportunities/${id}`, { stage: data?.stage ?? data }),
  getLeadActivities: (id) => api.get(`/admin/sales/leads/${id}/activities`),
  addLeadActivity: (id, data) => api.post(`/admin/sales/leads/${id}/activities`, data),

  getActivities: (params) => api.get('/admin/sales/activities', { params }),
  createActivity: (data) => api.post('/admin/sales/activities', data),
  completeActivity: (id, outcome) => api.put(`/admin/sales/activities/${id}/complete`, { outcome }),

  getSalesStats: (params) => api.get('/admin/sales/stats', { params }),

  // Marketing Contacts
  getMarketingContacts: (params) => api.get('/admin/marketing/contacts', { params }),
  getMarketingContact: (id) => api.get(`/admin/marketing/contacts/${id}`),
  createMarketingContact: (data) => api.post('/admin/marketing/contacts', data),
  updateMarketingContact: (id, data) => api.put(`/admin/marketing/contacts/${id}`, data),
  deleteMarketingContact: (id) => api.delete(`/admin/marketing/contacts/${id}`),
  unsubscribeContact: (id) => api.post(`/admin/marketing/contacts/${id}/unsubscribe`),
  importContacts: (data) => api.post('/admin/marketing/contacts/import', data),
  exportMarketingContacts: (params) => api.get('/admin/marketing/contacts/export', { params }),
  addContactActivity: (id, data) => api.post(`/admin/marketing/contacts/${id}/activities`, data),
  addContactNote: (id, data) => api.post(`/admin/marketing/contacts/${id}/notes`, data),
  syncTenantUsers: () => api.post('/admin/marketing/contacts/sync-tenant-users'),
  backfillContacts: () => api.post('/admin/marketing/contacts/backfill'),

  getLists: () => api.get('/admin/marketing/lists'),
  getMarketingLists: () => api.get('/admin/marketing/lists'),
  createList: (data) => api.post('/admin/marketing/lists', data),
  createMarketingList: (data) => api.post('/admin/marketing/lists', data),
  updateMarketingList: (id, data) => api.put(`/admin/marketing/lists/${id}`, data),
  deleteMarketingList: (id) => api.delete(`/admin/marketing/lists/${id}`),
  addContactsToList: (id, contactIds) => api.post(`/admin/marketing/lists/${id}/contacts`, { contactIds }),
  removeContactsFromList: (id, contactIds) => api.delete(`/admin/marketing/lists/${id}/contacts`, { data: { contactIds } }),

  getTemplates: (params) => api.get('/admin/marketing/templates', { params }),
  getEmailTemplates: (params) => api.get('/admin/marketing/templates', { params }),
  getTemplate: (id) => api.get(`/admin/marketing/templates/${id}`),
  createTemplate: (data) => api.post('/admin/marketing/templates', data),
  createEmailTemplate: (data) => api.post('/admin/marketing/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/marketing/templates/${id}`, data),
  updateEmailTemplate: (id, data) => api.put(`/admin/marketing/templates/${id}`, data),
  deleteEmailTemplate: (id) => api.delete(`/admin/marketing/templates/${id}`),
  previewTemplate: (id, data) => api.post(`/admin/marketing/templates/${id}/preview`, data),

  getCampaigns: (params) => api.get('/admin/marketing/campaigns', { params }),
  getCampaignStats: () => api.get('/admin/marketing/campaigns/stats'),
  getCampaign: (id) => api.get(`/admin/marketing/campaigns/${id}`),
  createCampaign: (data) => api.post('/admin/marketing/campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/marketing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/admin/marketing/campaigns/${id}`),
  scheduleCampaign: (id, scheduledAt) => api.post(`/admin/marketing/campaigns/${id}/schedule`, { scheduledAt }),
  sendCampaign: (id) => api.post(`/admin/marketing/campaigns/${id}/send`),
  pauseCampaign: (id) => api.post(`/admin/marketing/campaigns/${id}/pause`),

  getMarketingStats: () => api.get('/admin/marketing/stats'),

  // Analytics
  getRevenueAnalytics: (params) => api.get('/admin/analytics/revenue', { params }),
  getTenantAnalytics: (params) => api.get('/admin/analytics/tenants', { params }),
  getUsageAnalytics: (params) => api.get('/admin/analytics/usage', { params }),
  getSalesAnalytics: (params) => api.get('/admin/analytics/sales', { params }),
  getMarketingAnalytics: (params) => api.get('/admin/analytics/marketing', { params }),

  // Neon Database Usage
  getNeonUsage: (params) => api.get('/admin/neon-usage', { params }),
  getTenantNeonUsage: (tenantId, params) => api.get(`/admin/neon-usage/${tenantId}`, { params }),
  collectNeonUsage: (data) => api.post('/admin/neon-usage/collect', data),
  configureAutoSuspend: () => api.post('/admin/tenants/configure-auto-suspend'),

  // Support
  getSupportTickets: (params) => api.get('/admin/support/tickets', { params }),
  getTickets: (params) => api.get('/admin/support/tickets', { params }),
  getSupportTicket: (id) => api.get(`/admin/support/tickets/${id}`),
  getTicket: (id) => api.get(`/admin/support/tickets/${id}`),
  createTicket: (data) => api.post('/admin/support/tickets', data),
  updateTicket: (id, data) => api.put(`/admin/support/tickets/${id}`, data),
  updateTicketStatus: (id, status) => api.put(`/admin/support/tickets/${id}`, { status }),
  updateTicketPriority: (id, priority) => api.put(`/admin/support/tickets/${id}`, { priority }),
  assignTicket: (id, assignedToId) => api.post(`/admin/support/tickets/${id}/assign`, { assignedToId }),
  addComment: (id, data) => api.post(`/admin/support/tickets/${id}/comments`, data),
  replyToTicket: (id, data) => api.post(`/admin/support/tickets/${id}/comments`, data),
  getTicketMessages: (id) => api.get(`/admin/support/tickets/${id}/messages`),
  resolveTicket: (id, resolution) => api.post(`/admin/support/tickets/${id}/resolve`, { resolution }),
  getSupportStats: (params) => api.get('/admin/support/stats', { params }),

  // Admin Users
  getAdminUsers: () => api.get('/admin/users'),
  createAdminUser: (data) => api.post('/admin/users', data),
  updateAdminUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteAdminUser: (id) => api.delete(`/admin/users/${id}`),
  toggleAdminUserStatus: (id) => api.post(`/admin/users/${id}/toggle-status`),
  resetAdminPassword: (id) => api.post(`/admin/users/${id}/reset-password`),

  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  exportAuditLogs: (params) => api.get('/admin/audit-logs/export', { params, responseType: 'blob' }),

  // Firm database logins (portal plan phase 03): create or rotate the restricted pmp_app login the portal uses.
  setupAppRole: (slug) => api.post('/admin/db-credentials/app-role', { slug }),
  // Client portal file storage: the firm's R2 bucket and a bucket-scoped token (secret stored encrypted, never returned).
  setTenantStorage: (body) => api.post('/admin/security/storage', body),

  // Application settings (Settings -> General: general / email / billing / security / notifications)
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),

  // TEMPORARY one-time encryption key export (remove after use; see docs/security-review/KEY-EXPORT-RUNBOOK.md)
  getKeyExportStatus: () => api.get('/admin/key-export/status'),
  exportEncryptionKey: (data) => api.post('/admin/key-export', data),
  // Authenticator (TOTP) enrolment for the signed-in admin
  mfaSetup: () => api.post('/admin/auth/mfa/setup'),
  mfaVerify: (code) => api.post('/admin/auth/mfa/verify', { code }),
  mfaStatus: () => api.get('/admin/auth/mfa/status'),
  mfaSmsSetup: (mobile) => api.post('/admin/auth/mfa/sms/setup', mobile ? { mobile } : {}),
  mfaSmsVerify: (code) => api.post('/admin/auth/mfa/sms/verify', { code }),
  mfaDisable: (password) => api.post('/admin/auth/mfa/disable', { password }),
  resetAdminUserMfa: (id) => api.post(`/admin/users/${id}/reset-mfa`),

  // Platform settings (PMP's own legal entity)
  getPlatformSettings: () => api.get('/admin/platform-settings'),
  updatePlatformSettings: (data) => api.put('/admin/platform-settings', data),

  // Signup notifications (who hears about new trials, and how)
  getSignupNotificationSettings: () => api.get('/admin/signup-notifications'),
  updateSignupNotificationSettings: (data) => api.put('/admin/signup-notifications', data),
  testSignupNotification: (type) => api.post('/admin/signup-notifications/test', { type }),
  backfillSignupContacts: () => api.post('/admin/tenants/backfill-signup-contacts'),

  // Terms & Conditions
  getTerms: () => api.get('/admin/terms'),
  getTerm: (id) => api.get(`/admin/terms/${id}`),
  createTerm: (data) => api.post('/admin/terms', data),
  updateTerm: (id, data) => api.put(`/admin/terms/${id}`, data),
  deleteTerm: (id) => api.delete(`/admin/terms/${id}`),
  activateTerm: (id) => api.post(`/admin/terms/${id}/activate`),
};
